import { useState } from "react";
import { X, Printer, MessageCircle, CheckCircle2 } from "lucide-react";
import { fmtBRL, waLink } from "../lib/constants";
import { printCobrancaPDF } from "../lib/cobrancaReport";

/**
 * Modal to configure a "Cobrança" for a client and dispatch it as PDF and/or WhatsApp.
 *
 * @param {Object} props
 * @param {{ name: string, phone?: string }} props.client
 * @param {Array<{ kind: string, id?: string, orderNumber?: string, imei?: string, date?: string, description: string, items?: string[], value: number }>} props.rows
 * @param {{ from?: string, to?: string }} [props.period]
 * @param {{ storeName?: string, address?: string, phone?: string, cnpj?: string }} [props.store]
 * @param {(payload: { rows, discount, subtotal, client }) => void} [props.onClose_ThenSettle]  Called when user hits "Fechar com desconto" — parent should open PayModal with initialDiscount.
 * @param {() => void} props.onClose
 */
export default function CobrancaModal({ client, rows, period, store, onClose, onCloseThenSettle }) {
  const [discountStr, setDiscountStr] = useState("");
  const pct = Math.max(0, Math.min(100, parseFloat((discountStr || "").replace(",", ".")) || 0));
  const subtotal = rows.reduce((s, r) => s + (Number(r.value) || 0), 0);
  const discountValue = subtotal * (pct / 100);
  const total = subtotal - discountValue;

  const canSendWa = !!(client.phone && client.phone.trim());

  const buildWhatsAppMessage = () => {
    const lines = [];
    lines.push(`Olá ${client.name}! 👋`);
    lines.push("");
    lines.push(`*Cobrança — ${(store && store.storeName) || "Phone Lab"}*`);
    if (period && (period.from || period.to)) {
      lines.push(`Período: ${period.from || "—"} a ${period.to || "—"}`);
    }
    lines.push("");
    lines.push("*Itens:*");
    rows.forEach((r, i) => {
      const label = r.orderNumber ? `${r.orderNumber} — ` : (r.kind === "sale" ? "Peça: " : "");
      lines.push(`${i + 1}. ${label}${r.description || "—"}${r.imei ? ` (IMEI/OS: ${r.imei})` : ""}  →  R$ ${fmtBRL(r.value)}`);
    });
    lines.push("");
    if (pct > 0) {
      lines.push(`Subtotal: R$ ${fmtBRL(subtotal)}`);
      lines.push(`Desconto (${pct}%): -R$ ${fmtBRL(discountValue)}`);
    }
    lines.push(`*Total: R$ ${fmtBRL(total)}*`);
    lines.push("");
    lines.push("Obrigado pela preferência! 🙌");
    return lines.join("\n");
  };

  const openPDF = () => {
    printCobrancaPDF({ client, rows, period, store, discountPct: pct });
  };

  const sendWA = () => {
    if (!canSendWa) return;
    const url = waLink(client.phone, buildWhatsAppMessage());
    window.open(url, "_blank", "noopener");
  };

  const openBoth = () => {
    openPDF();
    if (canSendWa) sendWA();
  };

  const settleWithDiscount = () => {
    if (!onCloseThenSettle) return;
    onCloseThenSettle({ rows, discount: discountValue, subtotal, client });
  };
  const canSettle = !!onCloseThenSettle && rows.some((r) => !!r.id);

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="cobranca-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="font-heading font-bold text-slate-900 text-lg">Gerar cobrança</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              <b className="text-slate-800">{client.name}</b>
              {client.phone ? <span className="text-slate-400"> · {client.phone}</span> : <span className="text-rose-500 ml-1">· sem telefone</span>}
            </p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100 text-slate-400" data-testid="cobranca-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Items summary */}
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 max-h-40 overflow-y-auto">
            <div className="text-[11px] uppercase font-bold text-slate-500 tracking-wider mb-2">
              {rows.length} item(s) selecionado(s)
            </div>
            <ul className="space-y-1">
              {rows.map((r, i) => (
                <li key={i} className="flex items-center justify-between text-xs">
                  <span className="text-slate-700 truncate mr-2">
                    {r.orderNumber ? <span className="font-semibold text-blue-700">{r.orderNumber} </span> : null}
                    {r.description || "—"}
                  </span>
                  <span className="text-slate-800 font-semibold tabular-nums whitespace-nowrap">R$ {fmtBRL(r.value)}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Discount input */}
          <div>
            <label className="text-[11px] uppercase font-bold text-slate-500 tracking-wider">Desconto (%)</label>
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              <div className="relative flex-1 min-w-[120px]">
                <input
                  type="number"
                  inputMode="decimal"
                  min="0"
                  max="100"
                  step="0.5"
                  value={discountStr}
                  onChange={(e) => setDiscountStr(e.target.value)}
                  placeholder="0"
                  data-testid="cobranca-discount-input"
                  className="w-full h-10 rounded-lg border border-slate-300 pl-3 pr-8 text-sm focus:border-blue-500 focus:outline-none"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400 font-semibold">%</span>
              </div>
              <div className="inline-flex gap-1">
                {[5, 10, 15, 20, 50].map((p) => (
                  <button key={p}
                    onClick={() => setDiscountStr(String(p))}
                    data-testid={`cobranca-preset-${p}`}
                    className={`h-9 px-2.5 rounded-md text-[11px] font-semibold border transition-all ${
                      pct === p ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:border-blue-300 hover:text-blue-700"
                    }`}>
                    -{p}%
                  </button>
                ))}
                {discountStr && (
                  <button onClick={() => setDiscountStr("")} data-testid="cobranca-clear-discount"
                    className="h-9 px-2.5 rounded-md text-[11px] font-semibold border border-slate-200 text-slate-500 hover:border-rose-300 hover:text-rose-700">
                    Limpar
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Totals breakdown */}
          <div className="rounded-lg bg-gradient-to-br from-blue-600 to-blue-800 text-white p-4 space-y-1.5">
            {pct > 0 && (
              <>
                <div className="flex justify-between text-xs opacity-90">
                  <span>Subtotal</span>
                  <span className="tabular-nums">R$ {fmtBRL(subtotal)}</span>
                </div>
                <div className="flex justify-between text-xs text-rose-100">
                  <span>Desconto ({pct}%)</span>
                  <span className="tabular-nums">− R$ {fmtBRL(discountValue)}</span>
                </div>
                <div className="border-t border-white/20 my-1.5"></div>
              </>
            )}
            <div className="flex justify-between items-baseline">
              <span className="text-[11px] uppercase font-bold tracking-wider opacity-85">Total a receber</span>
              <span className="font-heading text-2xl font-extrabold tabular-nums" data-testid="cobranca-total">R$ {fmtBRL(total)}</span>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-2 flex-wrap">
          <button onClick={onClose} data-testid="cobranca-cancel"
            className="h-10 px-4 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-200 transition-colors">
            Cancelar
          </button>
          <button onClick={openPDF} data-testid="cobranca-generate-pdf"
            className="h-10 px-4 rounded-lg text-sm font-semibold bg-white border-2 border-blue-600 text-blue-700 hover:bg-blue-600 hover:text-white inline-flex items-center gap-1.5 transition-all">
            <Printer className="w-4 h-4" /> Gerar PDF
          </button>
          <button onClick={sendWA} disabled={!canSendWa} data-testid="cobranca-send-wa"
            title={canSendWa ? "Enviar cobrança pelo WhatsApp do cliente" : "Cliente sem telefone cadastrado"}
            className="h-10 px-4 rounded-lg text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1.5 transition-all disabled:opacity-40 disabled:cursor-not-allowed">
            <MessageCircle className="w-4 h-4" /> WhatsApp
          </button>
          <button onClick={openBoth} disabled={!canSendWa} data-testid="cobranca-both"
            title={canSendWa ? "Abrir PDF e WhatsApp ao mesmo tempo" : "Cliente sem telefone cadastrado"}
            className="h-10 px-4 rounded-lg text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 inline-flex items-center gap-1.5 transition-all disabled:opacity-40 disabled:cursor-not-allowed">
            <MessageCircle className="w-4 h-4" /> <Printer className="w-4 h-4" /> PDF + WA
          </button>
          {canSettle && (
            <button onClick={settleWithDiscount} data-testid="cobranca-settle"
              title="Fechar (marcar como pago) os itens já aplicando o desconto — o valor com desconto vai para o Financeiro"
              className="h-10 px-4 rounded-lg text-sm font-semibold bg-rose-600 text-white hover:bg-rose-700 inline-flex items-center gap-1.5 transition-all">
              <CheckCircle2 className="w-4 h-4" /> Fechar com desconto
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
